import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import PageHeading from '../../components/common/PageHeading';
import * as communityService from '../../services/community.service';
import * as hubAnnouncementService from '../../services/hubAnnouncement.service';
import MentionInput from '../../components/common/MentionInput';
import { stripMentionMarkers } from '../../utils/mentions';
import MediaPicker, { mediaUploading, mediaPayload } from '../../components/hive/MediaPicker';
import MediaGrid from '../../components/hive/MediaGrid';
import ContestEntryBox from '../../components/hive/ContestEntryBox';
import ReactionBar from '../../components/dashboard/ReactionBar';

export default function Discussion() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState(null);
  const [announcements, setAnnouncements] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searching, setSearching] = useState(false);

  const [composerBody, setComposerBody] = useState('');
  const [composerMedia, setComposerMedia] = useState([]);
  const [contests, setContests] = useState([]);
  const [composerContest, setComposerContest] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const [feed, recent] = await Promise.all([
        communityService.listFeed('recent'),
        hubAnnouncementService.getRecentAnnouncements().catch(() => []),
      ]);
      setPosts(feed);
      setAnnouncements(recent);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the discussion.');
    }
  }, []);

  useEffect(() => {
    load();
    // Landing on My Hive is what clears the nav badge — fire-and-forget,
    // not worth blocking the feed load on.
    communityService.markHiveViewed().catch(() => {});
    communityService.getRunningContests().then(setContests).catch(() => {});
  }, [load]);

  const submitPost = async (e) => {
    e.preventDefault();
    if (!composerBody.trim() || mediaUploading(composerMedia)) return;
    setPosting(true);
    try {
      await communityService.createPost({ body: composerBody.trim(), media: mediaPayload(composerMedia), contest_id: composerContest || null });
      setComposerBody('');
      setComposerMedia([]);
      setComposerContest('');
      // Reload rather than prepend the raw response — the create endpoint
      // doesn't attach author info, only the feed endpoint does.
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not post that.');
    } finally {
      setPosting(false);
    }
  };

  const submitSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      setSearchResults(await communityService.searchFeed(query.trim()));
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => { setQuery(''); setSearchResults(null); };

  const openPost = (post) => navigate(`/dashboard/discussion/${post.id}`);

  const handleReact = async (postId, type) => {
    try {
      const reactions = await communityService.reactToPost(postId, type);
      setPosts(prev => (prev || []).map(p => p.id === postId ? { ...p, reactions } : p));
      setSearchResults(prev => prev === null ? null : prev.map(p => p.id === postId ? { ...p, reactions } : p));
    } catch { /* non-critical */ }
  };

  const visiblePosts = searchResults !== null ? searchResults : posts;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-6">

        {/* ── Main feed ── */}
        <div className="flex-1 min-w-0 order-1">
          <div className="mb-6">
            <PageHeading title="My Hive" subtitle="Share ideas, share progress, voice your thoughts and opinions (with kindness)." />
          </div>

          <form onSubmit={submitPost} className="mb-6 bg-white rounded-2xl shadow-sm p-4">
            <MentionInput
              value={composerBody}
              onChange={setComposerBody}
              placeholder="Share something with the Hive… (type @ to tag someone)"
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
            <div className="mt-2">
              <MediaPicker items={composerMedia} setItems={setComposerMedia} />
            </div>
            <ContestEntryBox contests={contests} value={composerContest} onChange={setComposerContest} />
            <div className="flex justify-end mt-2">
              <button
                type="submit"
                disabled={posting || !composerBody.trim() || mediaUploading(composerMedia)}
                className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-lg hover:bg-reset-brown transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {posting ? 'Posting…' : mediaUploading(composerMedia) ? 'Uploading…' : 'Post'}
              </button>
            </div>
          </form>

          <form onSubmit={submitSearch} className="mb-6 flex gap-2">
            <input
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by topic…"
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-lg hover:bg-reset-brown transition-colors"
            >
              Search
            </button>
            {searchResults !== null && (
              <button type="button" onClick={clearSearch} className="px-3 py-2 text-sm text-gray-500 hover:text-reset-brown">
                Clear
              </button>
            )}
          </form>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
          )}

          {searchResults !== null && (
            <p className="text-xs text-gray-400 mb-3">
              {searching ? 'Searching…' : `${searchResults.length} result${searchResults.length === 1 ? '' : 's'} for "${query}"`}
            </p>
          )}

          {!visiblePosts ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : visiblePosts.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
              {searchResults !== null ? 'No posts match that search.' : 'Nothing here yet — check back soon.'}
            </div>
          ) : (
            <div className="space-y-6">
              {visiblePosts.map(post => (
                <PostCard key={post.id} post={post} onOpen={openPost} onReact={handleReact} />
              ))}
            </div>
          )}
        </div>

        {/* ── HMMMM? sidebar — thought-provoking prompts from the reset team ── */}
        <aside className="lg:w-72 shrink-0 order-2">
          <div className="bg-white rounded-2xl shadow-sm p-4 lg:sticky lg:top-6">
            <h2 className="text-sm font-bold text-reset-brown mb-3">HMMMM?</h2>
            {!announcements ? (
              <p className="text-xs text-gray-400">Loading…</p>
            ) : announcements.length === 0 ? (
              <p className="text-xs text-gray-400">Nothing here yet.</p>
            ) : (
              <div className="space-y-3">
                {announcements.slice(0, 7).map(a => (
                  <button
                    key={a.id}
                    onClick={() => navigate(`/dashboard/announcements/${a.id}`)}
                    className="flex gap-3 group text-left w-full"
                  >
                    <div className="w-14 h-14 rounded-lg bg-reset-sage/10 shrink-0 overflow-hidden flex items-center justify-center text-xl">
                      {a.image_url ? <img src={a.image_url} alt="" className="w-full h-full object-cover" /> : '📣'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-reset-brown leading-snug group-hover:underline">
                        {a.title || a.message_text}
                      </p>
                      <p className="text-[11px] text-reset-terracotta mt-0.5">Read more →</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={() => navigate('/dashboard/announcements')}
              className="block w-full text-center text-xs font-medium text-reset-terracotta hover:underline mt-4 pt-3 border-t border-gray-50"
            >
              View full archive →
            </button>
          </div>
        </aside>

      </div>
    </DashboardLayout>
  );
}

// ─── Post card ───────────────────────────────────────────────────────────────

function PostCard({ post, onOpen, onReact }) {
  const dateLabel = new Date(post.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

  // Staff "weekly piece" — blog-style card with image, title, blurb.
  if (post.title) {
    return (
      <article className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {post.cover_image_url && (
          <button onClick={() => onOpen(post)} className="block w-full">
            <img src={post.cover_image_url} alt={post.cover_image_alt || ''} className="w-full h-auto max-h-[28rem] object-contain bg-reset-creamDark" />
          </button>
        )}
        <div className="p-5">
          <p className="text-xs text-gray-400 mb-1">
            {dateLabel} · {post.author?.name || 'The Reset Team'}
          </p>
          <button onClick={() => onOpen(post)} className="text-left">
            <h2 className="text-xl font-bold text-reset-brown hover:underline">{post.title}</h2>
          </button>
          {post.excerpt && (
            <p className="text-sm text-gray-600 leading-relaxed mt-2">{post.excerpt}</p>
          )}
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={() => onOpen(post)}
              className="px-4 py-2 border border-reset-terracotta text-reset-terracotta text-sm font-medium rounded-xl hover:bg-reset-terracotta hover:text-white transition-colors"
            >
              Continue Reading
            </button>
            <div className="flex items-center gap-3">
              <ReactionBar reactions={post.reactions} onReact={(type) => onReact(post.id, type)} />
              <button onClick={() => onOpen(post)} className="text-xs text-gray-400 hover:text-reset-brown">
                {post.comment_count} comment{post.comment_count === '1' ? '' : 's'}
              </button>
            </div>
          </div>
        </div>
      </article>
    );
  }

  // Plain member post — simple text card.
  const initials = (post.author?.name || '?').trim().charAt(0).toUpperCase();
  return (
    <article className="bg-white rounded-2xl shadow-sm p-5">
      <div className="flex items-center gap-2.5 mb-2">
        {post.author?.avatar_url ? (
          <img src={post.author.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-reset-sage text-white flex items-center justify-center text-xs font-bold shrink-0">
            {initials}
          </div>
        )}
        <div>
          <p className="text-sm font-semibold text-reset-brown">{post.author?.name || '(deleted member)'}</p>
          <p className="text-[11px] text-gray-400">{dateLabel}</p>
        </div>
      </div>
      {/* Jenn, 2026-10-26: "Is there a reason why I can't click on a post... to have it open so that I can read the full
          text?" The text was cut to 4 lines and only the small comments link opened the post. Now the text opens it, and a
          clear link shows whenever the post is longer than the preview. */}
      <button type="button" onClick={() => onOpen(post)} className="block w-full text-left" aria-label={`Open the full post by ${post.author?.name || 'this member'}`}>
        <p className="text-sm text-gray-700 whitespace-pre-line line-clamp-4">{stripMentionMarkers(post.body)}</p>
      </button>
      {(stripMentionMarkers(post.body || '').length > 280 || (post.body || '').split('\n').length > 4) && (
        <button type="button" onClick={() => onOpen(post)} className="text-xs font-semibold text-reset-terracotta hover:underline mt-1">Read the full post →</button>
      )}
      {post.image_url && (
        <button onClick={() => onOpen(post)} className="block mt-3">
          <img src={post.image_url} alt="" className="max-h-72 rounded-xl object-cover" />
        </button>
      )}
      <MediaGrid media={post.media} />
      {post.contest_entry && <p className="text-[11px] text-reset-terracotta font-medium mt-2">🏆 Contest entry: {post.contest_entry}</p>}
      <div className="flex items-center justify-between mt-3">
        <ReactionBar reactions={post.reactions} onReact={(type) => onReact(post.id, type)} />
        <button onClick={() => onOpen(post)} className="text-xs text-reset-terracotta hover:underline">
          {post.comment_count > 0 ? `View ${post.comment_count} comment${post.comment_count === '1' ? '' : 's'}` : 'Be the first to comment'}
        </button>
      </div>
    </article>
  );
}
